import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ChevronGlyph, SearchGlyph} from '@/components/ui/icons'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {PickerOptionRow} from '@/components/employee-picker'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import {rankMatches} from '@/lib/ranking'

export function EmployeeViewAsCombobox({
  roster,
  value,
  onChange,
  testId,
}: {
  roster: Employee[]
  value: {id: string; name: string} | null
  onChange: (employee: Employee) => void
  testId: string
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const actives = useMemo(() => roster.filter((e) => e.status === 'active'), [roster])
  const matches = useMemo(() => rankMatches(actives, query), [actives, query])

  // Con la lente puesta el roster (solo operador) da 403 bajo la identidad prestada; el banner es la salida.
  const frozen = value !== null

  function handleOpenChange(next: boolean) {
    if (frozen) return
    setOpen(next)
    if (!next) setQuery('')
  }

  function pick(employee: Employee) {
    onChange(employee)
    setOpen(false)
    setQuery('')
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={frozen}
          data-testid={`${testId}-trigger`}
          className="flex min-h-11 w-full items-center justify-between gap-2 rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground disabled:cursor-default disabled:opacity-100 md:min-h-9">
          {value ? (
            <span className="min-w-0 truncate font-medium">{value.name}</span>
          ) : (
            <span className="min-w-0 truncate text-muted-foreground">{t('viewAs.placeholder')}</span>
          )}
          <ChevronGlyph size={14} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[var(--radix-popover-trigger-width)] overflow-hidden p-0"
        data-testid={testId}>
        <div className="flex items-center gap-2 border-b border-border px-2.5">
          <SearchGlyph className="shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('jerarquia.searchPerson')}
            autoComplete="off"
            autoFocus
            data-testid={`${testId}-search`}
            className="min-h-10 w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        <ul className="max-h-56 overflow-y-auto p-1">
          {actives.length === 0 && (
            <li className="px-2 py-1.5 text-sm text-muted-foreground">{t('jerarquia.noActivePeople')}</li>
          )}
          {actives.length > 0 && query.trim() && matches.length === 0 && (
            <li className="px-2 py-1.5 text-sm text-muted-foreground">{t('jerarquia.noMatches')}</li>
          )}
          {matches.map((person) => (
            <li key={person.id}>
              <PickerOptionRow person={person} testId={`${testId}-option`} onPick={() => pick(person)} />
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
